import styles from './template.css';
import template from './template';
import {AoflElement, customElement, property} from '@aofl/element';
import {reportLabels} from '../i18n-labels';

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: true,
  scales: {
    xAxis: {
      grid: {
        offsetGridLines: true
      },
    },
    yAxis: {
      ticks: {
        beginAtZero: 0,
        min: 0
      }
    }
  },
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        usePointStyle: true,
      }
    },
  }
};

const CHART_DATASET_DEFAULTS = [];
const CHART_DATA = {
  labels: [],
  datasets: []
};

const canvasSizeMap = [
  { // default
    height: '286',
    width: '284',
    mql: window.matchMedia('(max-width: 374.98px)')
  },
  { // xs
    height: '255',
    width: '310',
    mql: window.matchMedia('(min-width: 375px) and (max-width: 575.98px)')
  },
  { // sm
    height: '230',
    width: '512',
    mql: window.matchMedia('(min-width: 576px) and (max-width: 767.98px)')
  },
  { // md
    height: '230',
    width: '506',
    mql: window.matchMedia('(min-width: 768px) and (max-width: 991.98px)')
  },
  { // lg
    height: '230',
    width: '728',
    mql: window.matchMedia('(min-width: 992px) and (max-width: 1198.98px)')
  },
  { // xl
    height: '230',
    width: '936',
    mql: window.matchMedia('(min-width: 1200px)')
  }
];

/**
 * @summary BarGraph
 * @extends {AoflElement}
 */
@customElement('bar-graph')
class BarGraph extends AoflElement {
  /**
   * Creates an instance of BarGraph.
   */
  constructor() {
    super();
  }

  /**
   * @readonly
   */
  static is = 'bar-graph';

  @property({type: Object})
  chartOptions = {};

  @property({type: Array})
  chartDatasets = [];
  @property({type: Array})
  chartLabels = [];
  @property({type: Array})
  chartAriaLabels = [];
  @property({type: Object, attribute: false})
  chartData = CHART_DATA;
  @property({type: Array})
  chartPlugins = {};
  @property({type: String, attribute: false})
  graphWidth = this.offsetWidth;
  @property({type: String, attribute: false})
  graphHeight = this.offsetHeight;

  resizeHandler = () => {
    requestAnimationFrame(() => {
      this.graphWidth = this.offsetWidth;
    });
  };

  mqlHandler = () => {
    this.setGraphSize();
  };

  setGraphSize() {
    for (let i = 0; i < canvasSizeMap.length; i++) {
      const item = canvasSizeMap[i];
      if (item.mql.matches) {
        if (this.chartOptions.aspectRatio) {
          const graphHeight = item.width/this.chartOptions.aspectRatio;
          if (graphHeight < 130) {
            this.graphHeight = 130;
          } else {
            this.graphHeight = graphHeight;
          }
        } else {
          this.graphHeight = item.height;
        }
        break;
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    if (Object.keys(this.chartOptions).length === 0) {
      this.chartOptions = Object.assign({}, CHART_OPTIONS);
    }

    this.setGraphSize();
    for (let i = 0; i < canvasSizeMap.length; i++) {
      const item = canvasSizeMap[i];
      item.mql.addListener(this.mqlHandler);
    }
    window.addEventListener('resize', this.resizeHandler, {passive: true});
  }

  firstUpdated() {
    super.firstUpdated();
    this.resizeHandler();
  }

  shouldUpdate(changedProperties) {
    if (changedProperties.has('chartDatasets') || changedProperties.has('chartLabels')) {
      const datasets = [];
      const promises = [];

      for (let i = 0; i < this.chartDatasets?.length; i++) {
        const label = this.chartDatasets[i].label;
        if (typeof reportLabels[label] === 'undefined') {
          promises.push(Promise.resolve(label));
        } else {
          promises.push(reportLabels[label]);
        }
        datasets.push(Object.assign({}, CHART_DATASET_DEFAULTS[i], this.chartDatasets[i]));
      }

      Promise.all(promises).then((results) => {
        for (let i = 0; i < results.length; i++) {
          datasets[i].label = results[i];
        }
        this.chartData = Object.assign({}, CHART_DATA, {
          labels: this.chartLabels,
          ariaLabels: this.chartAriaLabels,
          datasets
        });
      });
    }
    if (changedProperties.has('chartOptions')) {
      this.chartOptions = Object.assign({}, CHART_OPTIONS, this.chartOptions);
    }

    return true;
  }

  /**
   *
   * @return {Object}
   */
  render() {
    return super.render(template, [styles]);
  }
  disconnectedCallback() {
    for (let i = 0; i < canvasSizeMap.length; i++) {
      const item = canvasSizeMap[i];
      item.mql.removeListener(this.mqlHandler);
    }
    window.removeEventListener('resize', this.resizeHandler);
    super.disconnectedCallback();
  }
}

export default BarGraph;
